import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Admin from './Admin';
import Archive from './Archive';
import CustomersPage from './CustomersPage';
import GlobalNoticeLayer from './GlobalNoticeLayer';
import OrderDetail from './OrderDetail';
import RoleWorkspacePage from './RoleWorkspacePage';
import OrdersWorkspace from './OrdersWorkspace';
import WorkshopRequestsPage from './WorkshopRequestsPage';
import Home from './Home';
import UsersPage from './UsersPage';
import TelegramScannerPage from './TelegramScannerPage';
import {
  hasTelegramWebAppSession,
  isTelegramEmployeeSessionTokenExpired,
  isTelegramWebApp,
  markTelegramWebAppSession,
  readTelegramUrlSessionToken,
  setTelegramEmployeeSessionToken,
  getTelegramEmployeeSessionToken,
} from './telegramWebApp';
import { apiFetch, parseJsonSafely } from './api';
import { canAccessPage, canAccessRole, clearAppAuthSession, getAppAuthMe, getAppAuthRole, getAppAuthToken, setAppAuthSession, subscribeToAppAuth } from './appAuth';
import { showGlobalError } from './globalErrors';
import { RoleConfigProvider } from './RoleConfigContext';
import './App.css';

const HEADER_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/kaznadzei-header-logo.png`;

function TelegramScannerTokenRouter() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const rawToken = String(token || '').trim();
    if (rawToken && rawToken.length > 32 && !isTelegramEmployeeSessionTokenExpired(rawToken)) {
      setTelegramEmployeeSessionToken(rawToken);
      markTelegramWebAppSession();
    }
    try {
      const pathname = String(location.pathname || '');
      const scanMatch = pathname.match(/^\/telegram-app\/t\/[^/]+\/scan\/(.+)$/);
      if (scanMatch && scanMatch[1]) {
        const nextBase = '/' + String(scanMatch[1]).replace(/^\/+/, '');
        let finalUrl;
        try {
          const u = new URL(nextBase, window.location?.origin || 'http://localhost');
          if (rawToken && rawToken.length > 32) {
            u.searchParams.set('employeeSessionToken', rawToken);
            const hashParams = new URLSearchParams(u.hash.replace(/^#/, ''));
            hashParams.set('token', rawToken);
            u.hash = hashParams.toString();
          }
          finalUrl = `${window.location.origin}${u.pathname}${u.search}${u.hash ? u.hash : ''}`;
        } catch (_) {
          finalUrl = (window.location?.origin || '') + nextBase;
        }
        window.location.replace(finalUrl);
        return undefined;
      }
    } catch (_) { /* ignore routing errors */ }
    navigate('/telegram-app', { replace: true });
  }, [token, navigate, location.pathname]);
  return null;
}

class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            errorMessage: '',
        };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            errorMessage: error?.message || 'Неизвестная ошибка интерфейса.',
        };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Application render error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="container">
                    <div className="card" style={{ maxWidth: 760, margin: '48px auto' }}>
                        <h2 style={{ marginTop: 0 }}>Интерфейс не смог загрузиться</h2>
                        <p style={{ marginBottom: 12 }}>
                            Приложение поймало ошибку во время рендера. Обновите страницу.
                            Если ошибка повторится, пришлите текст ниже.
                        </p>
                        <div className="settings-alert settings-alert-error" style={{ marginBottom: 12 }}>
                            {this.state.errorMessage}
                        </div>
                        <button className="btn btn-primary" type="button" onClick={() => window.location.reload()}>
                            Обновить страницу
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

function ProtectedRoute({ requiredRole, requiredPage, children }) {
    if (requiredPage) {
        if (!canAccessPage(requiredPage)) {
            return <Navigate to="/" replace />;
        }
        return children;
    }
    const authRole = getAppAuthRole();
    if (!canAccessRole(requiredRole, authRole)) {
        return <Navigate to="/" replace />;
    }
    return children;
}

function getAuthRoleLabel(role) {
    if (role === 'admin') return 'Администратор';
    if (role === 'manager') return 'Рабочий доступ';
    return '';
}

function AppLayout() {
    const location = useLocation();
    const routeTelegramMode = location.pathname === '/telegram-app';
    const ordersRoute = location.pathname === '/orders';
    const centeredHeaderLogoRoute = !ordersRoute;
    const telegramMode = isTelegramWebApp() || hasTelegramWebAppSession() || routeTelegramMode;
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [authRole, setAuthRole] = useState(() => getAppAuthRole());
    const [me, setMe] = useState(() => getAppAuthMe());
    const canAccessOrders = canAccessRole('manager', authRole);
    const mobileMenuAnchorRef = useRef(null);

    useEffect(() => {
        if (isTelegramWebApp()) {
            markTelegramWebAppSession();
        }
    }, [location.pathname]);

    useEffect(() => {
        document.documentElement.dataset.theme = 'light';
        document.documentElement.style.colorScheme = 'light';
        document.body.dataset.theme = 'light';
        window.localStorage.removeItem('kaznadzei.theme');
    }, []);

    useEffect(() => {
        const handleWindowError = (event) => {
            const message = event?.error || event?.message || 'Произошла ошибка интерфейса.';
            showGlobalError(message, 'Произошла ошибка интерфейса.');
        };

        const handleUnhandledRejection = (event) => {
            showGlobalError(event?.reason, 'Необработанная ошибка при выполнении действия.');
        };

        window.addEventListener('error', handleWindowError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleWindowError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!mobileMenuOpen) return undefined;

        const handlePointerDown = (event) => {
            const anchorNode = mobileMenuAnchorRef.current;
            if (anchorNode?.contains(event.target)) return;
            setMobileMenuOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        const syncAuth = () => {
            setAuthRole(getAppAuthRole());
            setMe(getAppAuthMe());
        };
        return subscribeToAppAuth(syncAuth);
    }, []);

    useEffect(() => {
        const token = getAppAuthToken();
        if (token) {
            setAuthRole(getAppAuthRole());
            setMe(getAppAuthMe());
            return undefined;
        }

        let cancelled = false;

        const tryExchangeEmployeeToken = async () => {
            const urlToken = readTelegramUrlSessionToken();
            const storedToken = getTelegramEmployeeSessionToken();
            const exchangeToken = urlToken || storedToken;
            if (!exchangeToken) return;

            if (urlToken) {
                setTelegramEmployeeSessionToken(urlToken);
                markTelegramWebAppSession();
            }

            try {
                const res = await apiFetch('/api/auth/employee-login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionToken: exchangeToken }),
                });
                const data = await parseJsonSafely(res);
                if (!res.ok) {
                    throw new Error(data?.message || 'Не удалось выполнить вход сотрудника.');
                }
                if (cancelled) return;
                setAppAuthSession({
                    sessionToken: data.sessionToken,
                    role: data.role,
                    me: data.me,
                    permissions: data.permissions,
                    fullAccess: false,
                });
                setAuthRole(data.role);
                setMe(data.me);
            } catch (error) {
                // Failure to exchange non-fatal: request will use fallback bearer header from api.js
            }
        };

        tryExchangeEmployeeToken();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const token = getAppAuthToken();
        if (!token) {
            setAuthRole('');
            setMe(null);
            return undefined;
        }

        let cancelled = false;
        apiFetch('/api/auth/session')
            .then(async (res) => {
                if (!res.ok) {
                    clearAppAuthSession();
                    return;
                }
                const data = await parseJsonSafely(res);
                if (!cancelled) {
                    setAuthRole(getAppAuthRole());
                    if (data?.me) {
                        setMe(data.me);
                    } else {
                            setMe(getAppAuthMe());
                        }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    clearAppAuthSession();
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const handleLogout = () => {
        clearAppAuthSession();
        setAuthRole('');
        setMe(null);
        setMobileMenuOpen(false);
    };

    const displayName = me?.employeeName
        ? me.employeeName
        : (me?.username || '');
    const roleLabel = me?.role?.name
        ? me.role.name
        : getAuthRoleLabel(authRole);
    let pagesCount = me?.pagesCount;
    if (!pagesCount && me?.role?.pages && typeof me.role.pages === 'object') {
        pagesCount = Object.values(me.role.pages).filter(Boolean).length;
    } else if (!pagesCount) {
        const permPages = me?.permissions && typeof me.permissions === 'object' ? me.permissions : null;
        const fullAccess = me?.fullAccess === true;
        if (fullAccess) pagesCount = 8;
        else if (permPages) pagesCount = Object.values(permPages).filter(Boolean).length;
    }
    const pagesSummary = pagesCount ? `${pagesCount}/8 стр.` : '';

    return (
        <>
            {!telegramMode && (
                <div
                    className={`App-header ${mobileMenuOpen ? 'App-header-mobile-open' : ''} ${centeredHeaderLogoRoute ? 'App-header-logo-centered' : ''}`}
                    style={{ '--header-logo-image': `url("${HEADER_LOGO_SRC}")` }}
                >
                    <div className="App-header-main">
                        <div className="App-header-orders-zone">
                            {canAccessOrders && ordersRoute ? (
                                <div className="App-header-orders-left-slot">
                                    <div id="orders-header-left-actions" className="App-header-orders-left-slot-inner" />
                                </div>
                            ) : null}
                            <Link
                                to={canAccessOrders ? '/orders' : '/'}
                                className="App-header-brand-link"
                                aria-label={canAccessOrders ? 'Перейти к заказам' : 'Перейти на главную'}
                                title={canAccessOrders ? 'К странице заказов' : 'На главную'}
                            />
                            {canAccessOrders && ordersRoute ? (
                                <div className="App-header-center">
                                    <div id="orders-header-primary-actions" className="App-header-orders-primary-slot" />
                                </div>
                            ) : null}
                        </div>
                        <div ref={mobileMenuAnchorRef} className="App-header-menu-anchor">
                            <button
                                className="mobile-menu-toggle"
                                type="button"
                                onClick={() => setMobileMenuOpen(current => !current)}
                                aria-expanded={mobileMenuOpen}
                                aria-label={mobileMenuOpen ? 'Скрыть меню навигации' : 'Показать меню навигации'}
                                title="Навигация"
                            >
                                <span className="mobile-menu-toggle-line" aria-hidden="true" />
                                <span className="mobile-menu-toggle-line" aria-hidden="true" />
                                <span className="mobile-menu-toggle-line" aria-hidden="true" />
                            </button>
                            <div className={`App-header-actions ${mobileMenuOpen ? 'App-header-actions-open' : ''}`}>
                                <nav className="App-header-nav App-header-nav-primary">
                                    {canAccessOrders && <Link to="/orders" onClick={() => setMobileMenuOpen(false)}>Заказы</Link>}
                                    {canAccessOrders && <Link to="/requests" onClick={() => setMobileMenuOpen(false)}>Заявки</Link>}
                                    {canAccessOrders && <Link to="/archive" onClick={() => setMobileMenuOpen(false)}>Архив</Link>}
                                    {canAccessOrders && <Link to="/customers" onClick={() => setMobileMenuOpen(false)}>Заказчики</Link>}
                                    {canAccessRole('admin', authRole) && <Link to="/settings?tab=employees" onClick={() => setMobileMenuOpen(false)}>Сотрудники</Link>}
                                    {canAccessRole('admin', authRole) && <Link to="/settings?tab=colors" onClick={() => setMobileMenuOpen(false)}>Этапы производства</Link>}
                                    {canAccessPage('users') && <Link to="/users" onClick={() => setMobileMenuOpen(false)}>Пользователи</Link>}
                                    {canAccessRole('admin', authRole) && <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>Настройки</Link>}
                                </nav>
                                <div className="App-header-menu-footer">
                                    {(displayName || roleLabel || pagesSummary) ? (
                                        <div className="App-header-user-indicator">
                                            {displayName ? (
                                                <div className="App-header-user-name">{displayName}</div>
                                            ) : null}
                                            <div className="App-header-user-role">
                                                {roleLabel || 'Рабочий доступ'}
                                                {pagesSummary ? ` · ${pagesSummary}` : ''}
                                            </div>
                                        </div>
                                    ) : null}
                                    {authRole ? (
                                        <button className="btn btn-secondary App-header-logout" type="button" onClick={handleLogout}>
                                            Выйти
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className={telegramMode ? 'container container-telegram' : 'container'}>
                <Routes>
                    <Route path='/settings' element={<ProtectedRoute requiredRole='admin'><Admin /></ProtectedRoute>} />
                    <Route path='/users' element={<ProtectedRoute requiredPage='users'><UsersPage /></ProtectedRoute>} />
                    <Route path='/admin' element={<Navigate to='/settings' replace />} />
                    <Route path='/orders' element={<ProtectedRoute requiredRole='manager'><OrdersWorkspace /></ProtectedRoute>} />
                    <Route path='/requests' element={<ProtectedRoute requiredRole='manager'><WorkshopRequestsPage /></ProtectedRoute>} />
                    <Route path='/manager' element={<Navigate to='/orders' replace />} />
                    <Route path='/archive' element={<ProtectedRoute requiredRole='manager'><Archive /></ProtectedRoute>} />
                    <Route path='/customers' element={<ProtectedRoute requiredRole='manager'><CustomersPage /></ProtectedRoute>} />
                    <Route path='/order/:id/item/:itemId' element={<OrderDetail />} />
                    <Route path='/order/:id' element={<OrderDetail />} />
                    <Route path='/role/:roleKey' element={<ProtectedRoute requiredRole='manager'><RoleWorkspacePage /></ProtectedRoute>} />
                    <Route path='/carpenter' element={<Navigate to='/role/carpenter' replace />} />
                    <Route path='/designer' element={<Navigate to='/role/designer' replace />} />
                    <Route path='/assembler' element={<Navigate to='/role/assembler' replace />} />
                    <Route path='/painter' element={<Navigate to='/role/painter' replace />} />
                    <Route path='/telegram-app/t/:token' element={<TelegramScannerTokenRouter />} />
                    <Route path='/telegram-app/t/:token/*' element={<TelegramScannerTokenRouter />} />
                    <Route path='/telegram-app' element={<TelegramScannerPage />} />
                    <Route path='/' element={<Home />} />
                </Routes>
            </div>
            <GlobalNoticeLayer />
        </>
    );
}

function App() {
    return (
        <RoleConfigProvider>
            <Router>
                <AppErrorBoundary>
                    <AppLayout />
                </AppErrorBoundary>
            </Router>
        </RoleConfigProvider>
    );
}

export default App;
